import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Check, PencilLine } from "lucide-react";

// Type-then-pick DPJP field (/ajukan; Master Data mapping). `options`: the Master DPJP of the chosen Departemen
// ({id, nama}). `value`: {dpjp_id, dpjp_nama, manual}. Typing searches while ignoring titles and punctuation
// ("wiras" finds "dr. Wirasena, Sp.B"). "Lainnya (isi manual)" sits at the bottom of the suggestions (or alone when
// nothing matches) and switches to a free-text name, stored unverified. Without options the field is manual only.
const LAINNYA = "__lainnya__";
const words = (s) => (s || "").toLowerCase().replace(/[^a-zÀ-ÿ\s]/g, " ").split(/\s+/).filter(Boolean);
const matches = (nama, q) => {
  const hay = words(nama).join(" ");
  return words(q).every((w) => hay.includes(w));
};

export default function DpjpCombobox({
  id, options, disabled = false, disabledText = "Pilih Departemen dulu", value, onChange, invalid = false,
  allowLainnya = true, manualOnly = false, testId = "dpjp", describedBy,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listId = useId();
  const manual = manualOnly || value.manual;

  const filtered = useMemo(() => {
    const q = value.dpjp_id ? "" : value.dpjp_nama;
    return options.filter((o) => matches(o.nama, q)).slice(0, 50);
  }, [options, value.dpjp_id, value.dpjp_nama]);
  const items = allowLainnya ? [...filtered, { id: LAINNYA }] : filtered;

  useEffect(() => { setActive(0); }, [value.dpjp_nama]);

  const pick = (o) => {
    if (o.id === LAINNYA) {
      onChange({ dpjp_id: null, dpjp_nama: value.dpjp_id ? "" : value.dpjp_nama, manual: true });
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      onChange({ dpjp_id: o.id, dpjp_nama: o.nama, manual: false });
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (manual) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && open && items[active]) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  if (disabled) {
    return <Input id={id} disabled placeholder={disabledText} className="bg-slate-50" data-testid={`${testId}-input`} />;
  }

  if (manual) {
    return (
      <div className="space-y-1.5">
        <Input id={id} ref={inputRef} value={value.dpjp_nama} maxLength={100} autoComplete="off" aria-invalid={invalid || undefined}
          aria-describedby={describedBy} placeholder="Ketik nama DPJP lengkap dengan gelar (mis. dr. Andi, Sp.B)"
          onChange={(e) => onChange({ dpjp_id: null, dpjp_nama: e.target.value, manual: true })}
          className={invalid ? "border-red-400 focus-visible:ring-red-300" : ""} data-testid={`${testId}-input`} />
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500" data-testid={`${testId}-manual-note`}>
          <span className="inline-flex items-center gap-1"><PencilLine size={12} /> Diisi manual, akan diverifikasi Admin.</span>
          {!manualOnly && options.length > 0 && (
            <button type="button" className="py-1 font-medium text-[#267B84] underline underline-offset-2"
              onClick={() => { onChange({ dpjp_id: null, dpjp_nama: "", manual: false }); setTimeout(() => inputRef.current?.focus(), 0); }}
              data-testid={`${testId}-back-to-list`}>
              Pilih dari daftar
            </button>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input id={id} ref={inputRef} role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={open && items[active] ? `${listId}-${active}` : undefined}
        aria-invalid={invalid || undefined} aria-describedby={describedBy}
        value={value.dpjp_nama} autoComplete="off" maxLength={100} placeholder="Ketik nama DPJP, lalu pilih dari daftar"
        onFocus={(e) => {
          setOpen(true);
          if (window.innerWidth < 640) e.target.scrollIntoView({ block: "start", behavior: "smooth" });   // room for the list above the keyboard
        }}
        onBlur={() => setOpen(false)}
        onChange={(e) => { onChange({ dpjp_id: null, dpjp_nama: e.target.value, manual: false }); setOpen(true); }}
        onKeyDown={onKeyDown}
        className={`${value.dpjp_id ? "pr-9" : ""} ${invalid ? "border-red-400 focus-visible:ring-red-300" : ""}`}
        data-testid={`${testId}-input`} />
      {value.dpjp_id && <Check size={16} className="absolute right-3 top-2.5 text-[#00B9AD]" aria-hidden="true" />}
      {open && (
        <ul id={listId} role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          onMouseDown={(e) => e.preventDefault()}            // keep focus in the input while choosing
          data-testid={`${testId}-list`}>
          {filtered.length === 0 && (
            <li className="px-3 py-2.5 text-sm text-slate-500" data-testid={`${testId}-empty`}>
              {options.length === 0 ? "Belum ada daftar DPJP untuk departemen ini." : "Nama tidak ditemukan di daftar."}
            </li>
          )}
          {items.map((o, i) => (
            <li key={o.id} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              onClick={() => pick(o)} onMouseEnter={() => setActive(i)}
              className={`flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-2 text-base sm:text-sm ${
                o.id === LAINNYA ? "border-t border-slate-100 text-[#267B84] font-medium" : "text-slate-800"} ${
                i === active ? "bg-[#00B9AD]/10" : ""}`}
              data-testid={o.id === LAINNYA ? `${testId}-lainnya` : `${testId}-option`}>
              {o.id === LAINNYA ? <><PencilLine size={14} className="shrink-0" /> Lainnya (isi manual)</> : (
                <>
                  <span className="min-w-0 flex-1 break-words">{o.nama}</span>
                  {o.id === value.dpjp_id && <Check size={14} className="shrink-0 text-[#00B9AD]" />}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
